import React from 'react';
import { Counter } from '@hub/components/counter';
import { Stack, Typography, useTheme } from '@mui/material';

export interface AnimatedCurrencyProps {
  value: number;
  fontSize?: number;
  textColor?: string;
  fontWeight?: 'normal' | 'bold' | number;
  compact?: boolean;
  currencySymbol?: string;
  showAnimation?: boolean;
  fontStyle?: 'normal' | 'italic' | 'oblique';
  showDecimals?: boolean;
  letterSpacing?: string | number;
}

/**
 * AnimatedCurrency component provides an out-of-the-box solution for displaying
 * currency values with smooth counter animations and proper comma formatting.
 *
 * Features:
 * - Automatic number formatting with commas
 * - Smooth digit animations using Counter component
 * - Compact mode for smaller displays
 * - Customizable styling and currency symbols
 */
export const AnimatedCurrency: React.FC<AnimatedCurrencyProps> = ({
  value,
  fontSize = 48,
  textColor,
  fontWeight = 'normal',
  compact = false,
  currencySymbol = '$',
  showAnimation = true,
  fontStyle = 'normal',
  showDecimals = true,
  letterSpacing = 0,
}) => {
  const theme = useTheme();

  // Use provided textColor or fallback to theme color
  const resolvedTextColor = textColor || theme.palette.text.primary;

  // Ensure we have a valid number and format it safely
  let validNumber = 0;
  try {
    validNumber = parseFloat(String(value)) || 0;
    if (isNaN(validNumber) || !isFinite(validNumber)) {
      validNumber = 0;
    }
  } catch {
    validNumber = 0;
  }

  // Determine if number is negative
  const isNegative = validNumber < 0;

  // Get absolute value for formatting
  const absoluteValue = Math.abs(validNumber);

  // Format number with or without decimals
  const formatNumber = (value: number) => {
    if (showDecimals) {
      return value.toFixed(2);
    }
    return Math.floor(value).toString();
  };

  // Break number into segments with commas
  const formatNumberWithCommas = (value: number) => {
    const numStr = formatNumber(value);
    const [integerPart, decimalPart] = numStr.split('.');

    // Process integer part
    const intStr = integerPart;

    // If integer part is less than 1000, return as single segment with decimals if needed
    if (intStr.length <= 3) {
      return showDecimals && decimalPart
        ? [integerPart, decimalPart]
        : [integerPart];
    }

    const segments = [];
    // Split integer part into 3-digit groups from right to left
    for (let i = intStr.length; i > 0; i -= 3) {
      const start = Math.max(0, i - 3);
      const segment = intStr.slice(start, i);
      segments.unshift(segment);
    }

    // Add decimal part if needed
    if (showDecimals && decimalPart) {
      segments.push(decimalPart);
    }

    return segments;
  };

  // Calculate places for a specific segment
  const calculatePlacesForSegment = (segment: string) => {
    const places = [];
    for (let i = segment.length - 1; i >= 0; i--) {
      places.push(Math.pow(10, i));
    }
    return places;
  };

  // For when animation is disabled, render static text
  if (!showAnimation) {
    const compactFontSize = compact ? 18 : fontSize;

    // Format with proper sign handling and decimals
    const formatStaticValue = (value: number) => {
      const absValue = Math.abs(value);
      const formattedNumber = showDecimals
        ? absValue.toFixed(2)
        : Math.floor(absValue).toString();
      const withCommas = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      const sign = value < 0 ? '-' : '';
      return `${sign}${currencySymbol}${withCommas}`;
    };

    return (
      <Typography
        variant="body1"
        fontWeight={
          fontWeight === 'bold'
            ? 700
            : fontWeight === 'normal'
              ? 400
              : fontWeight
        }
        fontStyle={fontStyle}
        sx={{
          color: resolvedTextColor,
          fontSize: compactFontSize,
          lineHeight: 1.2,
          letterSpacing,
        }}
      >
        {formatStaticValue(validNumber)}
      </Typography>
    );
  }

  // For animated mode, use Counter components with comma separation
  const segments = formatNumberWithCommas(absoluteValue);

  return (
    <Stack direction="row" alignItems="center" gap={0} sx={{ letterSpacing }}>
      {/* Render negative sign if needed */}
      {isNegative && (
        <Typography
          component="span"
          fontWeight={
            fontWeight === 'bold'
              ? 700
              : fontWeight === 'normal'
                ? 400
                : fontWeight
          }
          fontStyle={fontStyle}
          sx={{
            color: resolvedTextColor,
            fontSize: `${fontSize}px !important`,
            letterSpacing,
            lineHeight: 1,
            fontFamily: "'General Sans', sans-serif",
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          -
        </Typography>
      )}
      {/* Currency symbol */}
      <Typography
        component="span"
        fontWeight={
          fontWeight === 'bold'
            ? 700
            : fontWeight === 'normal'
              ? 400
              : fontWeight
        }
        fontStyle={fontStyle}
        sx={{
          color: resolvedTextColor,
          fontSize: `${fontSize}px !important`,
          letterSpacing,
          lineHeight: 1,
          fontFamily: "'General Sans', sans-serif",
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {currencySymbol}
      </Typography>
      {segments.map((segment, index) => {
        const isDecimalPart =
          showDecimals && index === segments.length - 1 && segments.length > 1;
        const isLastIntegerSegment = showDecimals
          ? index === segments.length - 2
          : index === segments.length - 1;

        return (
          <Stack key={index} direction="row" alignItems="center" gap={0}>
            {/* Add decimal point before decimal segment */}
            {isDecimalPart && (
              <Typography
                component="span"
                fontWeight={
                  fontWeight === 'bold'
                    ? 700
                    : fontWeight === 'normal'
                      ? 400
                      : fontWeight
                }
                fontStyle={fontStyle}
                sx={{
                  color: resolvedTextColor,
                  fontSize: `${fontSize}px !important`,
                  marginX: 0,
                  letterSpacing,
                  lineHeight: 1,
                  fontFamily: "'General Sans', sans-serif",
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                .
              </Typography>
            )}
            <Counter
              value={parseInt(segment)}
              fontSize={fontSize}
              places={calculatePlacesForSegment(segment)}
              textColor={resolvedTextColor}
              fontWeight={fontWeight}
              gap={0}
              horizontalPadding={0}
              containerStyle={{
                fontStyle: fontStyle,
                letterSpacing,
              }}
              counterStyle={{
                background: 'transparent',
                letterSpacing,
                paddingRight: fontStyle === 'italic' ? '0.15ch' : '0.1ch',
              }}
              digitStyle={{
                marginRight: 0,
              }}
              gradientHeight={0}
            />
            {/* Add comma separator between integer segments */}
            {!isLastIntegerSegment && !isDecimalPart && (
              <Typography
                component="span"
                fontWeight={
                  fontWeight === 'bold'
                    ? 700
                    : fontWeight === 'normal'
                      ? 400
                      : fontWeight
                }
                fontStyle={fontStyle}
                sx={{
                  color: resolvedTextColor,
                  fontSize: `${fontSize}px !important`,
                  marginX: 0,
                  letterSpacing,
                  lineHeight: 1,
                  fontFamily: "'General Sans', sans-serif",
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                ,
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
